import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import {
  arrowBackOutline,
  homeOutline,
  sendOutline,
} from "ionicons/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
  ChatConversation,
  ChatMessage,
  enviarMensaje,
  listarConversaciones,
  listarMensajes,
  marcarConversacionLeida,
} from "src/services/chat";

import "./Mensajes.css";

type RouteParams = {
  id: string;
};

type LocalPending = {
  tempId: string;
  cuerpo: string;
  creado_en: string;
};

const ChatConversacion: React.FC = () => {
  const history = useHistory();
  const [present] = useIonToast();
  const { id } = useParams<RouteParams>();
  const conversacionId = Number(id || 0);

  const [loading, setLoading] = useState(true);
  const [conversacion, setConversacion] = useState<ChatConversation | null>(null);
  const [mensajes, setMensajes] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<LocalPending[]>([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const ultimoLeidoMarcadoRef = useRef<number>(0);

  const scrollBottom = () => {
    if (!endRef.current) return;
    endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const horaMensaje = (msg: { hora_envio?: string; creado_en?: string }) => {
    if (msg?.hora_envio) return msg.hora_envio;
    const fecha = msg?.creado_en ? new Date(msg.creado_en) : null;
    if (!fecha || Number.isNaN(fecha.getTime())) return "";
    return fecha.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const cargarConversacion = async () => {
    try {
      const convData = await listarConversaciones();
      const actual = convData.find((c) => Number(c.id) === conversacionId) || null;
      setConversacion(actual);
    } catch {
      // no bloquear flujo de mensajes por falla de metadata
    }
  };

  const cargarMensajes = async (mostrarLoading: boolean = false) => {
    if (!conversacionId) return;
    if (mostrarLoading) setLoading(true);
    try {
      const msgData = await listarMensajes(conversacionId, { limit: 120 });
      setMensajes(msgData);

      const ultimo = msgData[msgData.length - 1];
      if (ultimo?.id && ultimo.id !== ultimoLeidoMarcadoRef.current) {
        ultimoLeidoMarcadoRef.current = ultimo.id;
        await marcarConversacionLeida(conversacionId, ultimo.id);
      }
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo cargar la conversación.",
        duration: 2800,
        color: "danger",
      });
    } finally {
      if (mostrarLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (!conversacionId) return;
    cargarConversacion();
    cargarMensajes(true);
  }, [conversacionId]);

  useEffect(() => {
    if (!conversacionId) return;
    let activo = true;

    const run = async () => {
      if (!activo) return;
      if (document.visibilityState !== "visible") return;
      await cargarMensajes(false);
    };

    const timer = setInterval(run, 2500);

    return () => {
      activo = false;
      if (timer) clearInterval(timer);
    };
  }, [conversacionId]);

  useEffect(() => {
    scrollBottom();
  }, [mensajes.length, pending.length]);

  const enviar = async () => {
    const body = texto.trim();
    if (!body || enviando || !conversacionId) return;

    const tempId = `tmp-${Date.now()}`;
    const localPending: LocalPending = {
      tempId,
      cuerpo: body,
      creado_en: new Date().toISOString(),
    };

    setTexto("");
    setPending((prev) => [...prev, localPending]);
    setEnviando(true);

    try {
      const created = await enviarMensaje(conversacionId, body);
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
      if (created) {
        setMensajes((prev) => [
          ...prev,
          {
            id: created.id,
            conversacion_id: created.conversacion_id,
            remitente_cedula: created.remitente_cedula,
            cuerpo: created.cuerpo,
            creado_en: new Date().toISOString(),
            es_mio: true,
            leido_por_otros: 0,
            total_otros: 1,
            leido_por_todos: false,
          },
        ]);
      }
      await cargarMensajes(false);
    } catch (error: any) {
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
      setTexto(body);
      present({
        message: error?.response?.data?.error || "No se pudo enviar el mensaje.",
        duration: 2800,
        color: "danger",
      });
    } finally {
      setEnviando(false);
    }
  };

  const nombreConversacion = useMemo(
    () => conversacion?.nombre_mostrar || "Conversación",
    [conversacion]
  );

  const esGrupo = conversacion?.tipo === "GRUPO";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton onClick={() => history.push("/mensajes")}> 
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>{nombreConversacion}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push("/home")}> 
              <IonIcon icon={homeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent id="mensajes" className="chat-content" fullscreen>
        {loading ? (
          <div className="mensajes-loading">
            <IonSpinner name="crescent" />
            <IonLabel>Cargando conversación...</IonLabel>
          </div>
        ) : (
          <IonList className="chat-messages-list">
            {mensajes.map((msg) => {
              const estado = msg.es_mio
                ? (msg.leido_por_todos ? "leído" : "enviado")
                : "";
              const hora = horaMensaje(msg);
              const remitente = msg.es_mio ? "Tú" : (msg.remitente_nombre || `Usuario ${msg.remitente_cedula}`);

              return (
                <div key={msg.id} className={msg.es_mio ? "msg-row own" : "msg-row other"}>
                  <div className={msg.es_mio ? "msg-bubble own" : "msg-bubble other"}>
                    {esGrupo && !msg.es_mio && (
                      <div className="msg-sender">{remitente}</div>
                    )}
                    <div className="msg-text">{msg.cuerpo}</div>
                    <div className="msg-meta">{hora}{estado ? ` · ${estado}` : ""}</div>
                  </div>
                </div>
              );
            })}

            {pending.map((msg) => (
              <div key={msg.tempId} className="msg-row own">
                <div className="msg-bubble own">
                  <div className="msg-text">{msg.cuerpo}</div>
                  <div className="msg-meta">{horaMensaje(msg)} · pendiente</div>
                </div>
              </div>
            ))}

            <div ref={endRef} />
          </IonList>
        )}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonItem lines="none">
            <IonInput
              value={texto}
              placeholder="Escribe un mensaje"
              onIonChange={(e) => setTexto(String(e.detail.value || ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  enviar();
                }
              }}
            />
            <IonButton slot="end" onClick={enviar} disabled={!texto.trim() || enviando}>
              <IonIcon icon={sendOutline} />
            </IonButton>
          </IonItem>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default ChatConversacion;
